import type { ActionScope } from "@/object-actions/types";
import type { FileRecord } from "@cohesive/api-client";

/**
 * True iff this record has ever been published. Doesn't care whether the
 * current content matches the published snapshot — use
 * `fileRecordIsPublishedAndCurrent` for that.
 */
export function fileRecordEverPublished(
  record: FileRecord | null | undefined,
): boolean {
  return !!record?.published;
}

/**
 * True iff this record is published AND the published snapshot matches the
 * current content (i.e. there are no unpublished changes on this record
 * itself). Hash comparison is skipped when either side is missing — older
 * records pre-date the hash field but were still considered "current" by
 * the legacy UI, so we preserve that behavior.
 */
export function fileRecordIsPublishedAndCurrent(
  record: FileRecord | null | undefined,
): boolean {
  if (!record?.published) return false;
  if (!record.published_content_hash || !record.content_hash) return true;
  return record.published_content_hash === record.content_hash;
}

export function fileRecordDisplayName(record: FileRecord): string {
  const segments = record.path?.split("/").filter(Boolean) ?? [];
  return segments[segments.length - 1] || record.id;
}

/**
 * Three-state visual contract for the publish trigger / dialog action.
 * Mirrors `ArtifactPane` styling.
 */
export type PublishVisualState = "unpublished" | "stale" | "up-to-date";

export interface PublishButtonStyling {
  className: string;
  label: string;
  tooltip: string;
  showPulse: boolean;
}

export function getPublishButtonStyling(
  state: PublishVisualState,
): PublishButtonStyling {
  switch (state) {
    case "unpublished":
      return {
        className:
          "bg-primary/15 text-primary dark:bg-primary/20 dark:text-primary hover:bg-primary/25 dark:hover:bg-primary/30",
        label: "Publish",
        tooltip: "Publish to create a shareable public URL",
        showPulse: false,
      };
    case "stale":
      return {
        className:
          "bg-amber-100 text-amber-800 dark:bg-lc-warning/20 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-lc-warning/30",
        label: "Publish updates",
        tooltip: "Linked files or content have changed since the last publish",
        showPulse: true,
      };
    case "up-to-date":
      return {
        className:
          "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 cursor-default",
        label: "Published",
        tooltip: "All changes published",
        showPulse: false,
      };
  }
}

/**
 * Solid-fill swatch class for the small status dot rendered next to the
 * state label inside the publish popover. Colors mirror the trigger button
 * styling so the popover reads as an extension of the same control.
 */
export function getPublishStatusDotClass(state: PublishVisualState): string {
  switch (state) {
    case "unpublished":
      return "bg-muted-foreground/60";
    case "stale":
      return "bg-lc-warning";
    case "up-to-date":
      return "bg-emerald-500";
  }
}

export function derivePublishVisualState({
  documentEverPublished,
  isFullyUpToDate,
}: {
  /** Has the document ever been published (regardless of current diff)? */
  documentEverPublished: boolean;
  /** Is the published snapshot current AND are all linked files published? */
  isFullyUpToDate: boolean;
}): PublishVisualState {
  if (!documentEverPublished) return "unpublished";
  if (!isFullyUpToDate) return "stale";
  return "up-to-date";
}

/** File records that can expose the publish action at all. */
export function fileRecordPublishEligible(record: FileRecord): boolean {
  if (!record.id) return false;
  if (record.path?.endsWith("/")) return false;
  if (record.status === "deleted" || record.status === "pending") return false;
  return true;
}

export function resolveFileRecordShowPublish(params: {
  record: FileRecord;
  canEdit?: boolean;
  scope?: ActionScope;
}): boolean {
  if (params.canEdit === false) return false;
  if (params.scope === "shared-with-me") {
    return false;
  }
  return fileRecordPublishEligible(params.record);
}
